import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router';
import { Header } from 'components/common/header/Header';
import { Button } from 'components/common/button/Button';
import { useAiRecommendSchedule, useConfirmSchedule } from 'hooks/useSchedule';
import { scheduleStore } from 'store/scheduleStore';
import './ScheduleResult.css';

export const ScheduleResult = () => {
    const navigate = useNavigate();
    const { meetingId } = useParams();
    const { data: aiRecommendData } = useAiRecommendSchedule();
    const { status: confirmStatus, data: confirmData } = useConfirmSchedule();

    // 컴포넌트 마운트 시 AI 추천 일정 조회
    useEffect(() => {
        if (meetingId) {
            scheduleStore.onGetAiRecommendSchedule(meetingId);
        }
    }, [meetingId]);

    // API 데이터를 optimalSlots 형식으로 변환
    const optimalSlots = aiRecommendData?.recommendations?.map((rec: any) => ({
        id: rec.scheduleId,
        date: new Date(rec.startDateTime).toLocaleDateString('ko-KR', {
            month: 'long',
            day: 'numeric',
            weekday: 'short'
        }),
        time: `${new Date(rec.startDateTime).toLocaleTimeString('ko-KR', {
            hour: '2-digit',
            minute: '2-digit'
        })} - ${new Date(rec.endDateTime).toLocaleTimeString('ko-KR', {
            hour: '2-digit',
            minute: '2-digit'
        })}`,
        availableCount: rec.availableCount,
        totalCount: rec.totalCount,
        participants: rec.availableMembers?.map((m: any) => m.nickname) || [],
        scheduleId: rec.scheduleId
    })) || [];

    const [selectedSlot, setSelectedSlot] = useState(optimalSlots[0] || null);

    // optimalSlots가 업데이트되면 첫 번째 슬롯 선택
    useEffect(() => {
        if (optimalSlots.length > 0 && !selectedSlot) {
            setSelectedSlot(optimalSlots[0]);
        }
    }, [optimalSlots, selectedSlot]);

    const handleConfirmTime = () => {
        if (selectedSlot) {
            scheduleStore.onConfirmSchedule(selectedSlot.scheduleId);
        }
    };

    // 일정 확정 성공 시 모임 상세로 이동
    useEffect(() => {
        if (confirmStatus === 'ready' && confirmData?.success) {
            navigate(`/meeting/detail/${meetingId}`, { replace: true });
        }
    }, [confirmStatus, confirmData, navigate, meetingId]);

    // 로딩 중이거나 데이터가 없는 경우 처리
    if (!selectedSlot || optimalSlots.length === 0) {
        return (
            <div className="schedule-result-wrapper">
                <Header title="최적 시간" isSubPage />
                <div className="schedule-result-content">
                    <div className="result-banner">
                        <div className="banner-icon">⏳</div>
                        <div className="banner-info">
                            <h2 className="banner-title">투표 결과를 분석 중입니다...</h2>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="schedule-result-wrapper">
            <Header title="최적 시간" isSubPage />

            <div className="schedule-result-content">
                {/* Success Banner */}
                <div className="result-banner">
                    <div className="banner-icon">🎉</div>
                    <div className="banner-info">
                        <h2 className="banner-title">최적의 시간을 찾았어요!</h2>
                        <p className="banner-description">
                            가장 많은 참여자가 가능한 시간이에요
                        </p>
                    </div>
                </div>

                {/* Top Recommendation */}
                <div className="top-recommendation">
                    <div className="recommendation-label">
                        <span className="label-icon">⭐</span>
                        <span className="label-text">가장 추천하는 시간</span>
                    </div>

                    <div className="recommendation-card">
                        <div className="card-header">
                            <div className="card-date">
                                <div className="date-icon">📅</div>
                                <div className="date-text">{selectedSlot.date}</div>
                            </div>
                            <div className="card-badge">
                                {selectedSlot.availableCount === selectedSlot.totalCount ? '전원 가능' : `${selectedSlot.availableCount}명 가능`}
                            </div>
                        </div>

                        <div className="card-time">
                            <div className="time-icon">⏰</div>
                            <div className="time-text">{selectedSlot.time}</div>
                        </div>

                        <div className="card-participants">
                            <div className="participants-header">
                                <span className="participants-icon">👥</span>
                                <span className="participants-title">참여 가능한 멤버</span>
                            </div>
                            <div className="participants-list">
                                {selectedSlot.participants.map((name: string, index: number) => (
                                    <div key={index} className="participant-chip">
                                        <span className="participant-avatar">👤</span>
                                        <span className="participant-name">{name}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Other Options */}
                <div className="other-options">
                    <h3 className="options-title">다른 가능한 시간</h3>
                    <div className="options-list">
                        {optimalSlots.slice(1).map((slot: any) => (
                            <button
                                key={slot.id}
                                className={`option-card ${selectedSlot.id === slot.id ? 'selected' : ''}`}
                                onClick={() => setSelectedSlot(slot)}
                            >
                                <div className="option-header">
                                    <div className="option-date-time">
                                        <div className="option-date">{slot.date}</div>
                                        <div className="option-time">{slot.time}</div>
                                    </div>
                                    <div className="option-count">
                                        <span className="count-number">{slot.availableCount}</span>
                                        <span className="count-label">/{slot.totalCount}명</span>
                                    </div>
                                </div>

                                <div className="option-participants">
                                    {slot.participants.map((name: string, index: number) => (
                                        <span key={index} className="option-participant-name">
                                            {name}
                                            {index < slot.participants.length - 1 && ', '}
                                        </span>
                                    ))}
                                </div>
                            </button>
                        ))}
                    </div>
                </div>

                {/* Info Notice */}
                <div className="result-notice">
                    <div className="notice-icon">💡</div>
                    <div className="notice-text">
                        시간을 확정하면 모든 참여자에게 알림이 전송됩니다
                    </div>
                </div>
            </div>

            {/* Action Buttons */}
            <div className="schedule-result-footer">
                <Button
                    text="이 시간으로 확정하기"
                    onPress={handleConfirmTime}
                />
            </div>
        </div>
    );
};
